import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit, Trash2, ArrowLeft, Menu } from 'lucide-react';
import toast from 'react-hot-toast';

interface Site {
  _id: string;
  name: string;
  code: string;
  address: string;
  postcode: string;
  city: string;
  phone: string;
  email?: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface SiteFormData {
  name: string;
  code: string;
  address: string;
  postcode: string;
  city: string;
  phone: string;
  email: string;
  isActive: boolean;
}

const emptyForm: SiteFormData = {
  name: '',
  code: '',
  address: '',
  postcode: '',
  city: '',
  phone: '',
  email: '',
  isActive: true,
};

const SiteManagement: React.FC = () => {
  const [sites, setSites] = useState<Site[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingSite, setEditingSite] = useState<Site | null>(null);
  const [formData, setFormData] = useState<SiteFormData>(emptyForm);

  const [assignUserId, setAssignUserId] = useState('');
  const [assignSiteId, setAssignSiteId] = useState('');

  const token = useMemo(() => localStorage.getItem('token'), []);

  const resetForm = () => {
    setEditingSite(null);
    setFormData(emptyForm);
  };

  const fetchSites = async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/sites', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data?.error || 'Failed to load sites');
        setSites([]);
        return;
      }
      setSites(Array.isArray(data) ? data : data.sites || []);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load sites');
      setSites([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    resetForm();
    setShowModal(true);
  };

  const openEdit = (site: Site) => {
    setEditingSite(site);
    setFormData({
      name: site.name || '',
      code: site.code || '',
      address: site.address || '',
      postcode: site.postcode || '',
      city: site.city || '',
      phone: site.phone || '',
      email: site.email || '',
      isActive: site.isActive ?? true,
    });
    setShowModal(true);
  };

  const saveSite = async () => {
    try {
      const isEdit = Boolean(editingSite?._id);
      const url = isEdit
        ? `/api/sites/${editingSite!._id}`
        : '/api/sites';

      const res = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...formData,
          email: formData.email.trim() ? formData.email.trim() : undefined,
        }),
      });

      const data = await res.json();
      if (!res.ok) {
        toast.error(data?.error || 'Failed to save site');
        return;
      }

      toast.success(isEdit ? 'Site updated' : 'Site created');
      setShowModal(false);
      resetForm();
      await fetchSites();
    } catch (e) {
      console.error(e);
      toast.error('Failed to save site');
    }
  };

  const deleteSite = async (siteId: string) => {
    const ok = window.confirm('Delete this site?');
    if (!ok) return;

    try {
      const res = await fetch(`/api/sites/${siteId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data?.error || 'Failed to delete site');
        return;
      }
      toast.success('Site deleted');
      await fetchSites();
    } catch (e) {
      console.error(e);
      toast.error('Failed to delete site');
    }
  };

  const assignSiteToUser = async () => {
    const userId = assignUserId.trim();
    const siteId = assignSiteId.trim();

    if (!userId) {
      toast.error('User ID is required');
      return;
    }
    if (!siteId) {
      toast.error('Select a site');
      return;
    }

    try {
      const res = await fetch(`/api/sites/users/${userId}/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ siteId }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data?.error || 'Failed to assign site');
        return;
      }
      toast.success('Site assigned to user');
      setAssignUserId('');
    } catch (e) {
      console.error(e);
      toast.error('Failed to assign site');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="sm:flex sm:justify-between sm:items-center mb-8">
          <div className="sm:flex-auto">
            <div className="flex items-center space-x-4">
              <Link
                to="/admin"
                className="inline-flex items-center text-gray-500 hover:text-gray-700 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Link>
              <img src="/logo.jpeg" alt="Logo" className="h-14 w-auto rounded" />
              <div>
                <p className="mt-2 text-sm text-gray-700">Create and manage your laundrette sites</p>
              </div>
            </div>
          </div>

          <div className="mt-4 sm:mt-0 sm:ml-4 sm:flex-none">
            <div className="flex space-x-4">
              <Link
                to="/admin"
                className="px-4 py-2 text-sm font-medium rounded-md bg-white text-gray-700 border border-gray-300 hover:bg-gray-50"
              >
                <Menu className="h-5 w-5 mr-2" />
                Admin Menu
              </Link>
              <button
                onClick={openCreate}
                className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Site
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg overflow-hidden mb-8">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Sites</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Code</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Address</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sites.length === 0 ? (
                  <tr>
                    <td className="px-6 py-6 text-sm text-gray-500" colSpan={6}>
                      No sites found
                    </td>
                  </tr>
                ) : (
                  sites.map((s) => (
                    <tr key={s._id}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{s.name}</div>
                        <div className="text-xs text-gray-500">{s._id}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{s.code}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                        {s.address}, {s.city} {s.postcode}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{s.phone}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${(s.isActive ?? true)
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                            }`}
                        >
                          {(s.isActive ?? true) ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => openEdit(s)}
                          className="text-primary-600 hover:text-primary-900 mr-4"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4 inline" />
                        </button>
                        <button
                          onClick={() => deleteSite(s._id)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4 inline" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Assign Site to User</h2>
            <p className="mt-1 text-sm text-gray-600">Paste a user ID and select a site to set that user’s site assignment.</p>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">User ID</label>
              <input
                value={assignUserId}
                onChange={(e) => setAssignUserId(e.target.value)}
                className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="e.g. 65f..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Site</label>
              <select
                value={assignSiteId}
                onChange={(e) => setAssignSiteId(e.target.value)}
                className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
              >
                <option value="">Select a site</option>
                {sites.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.name} ({s.code})
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-end">
              <button onClick={assignSiteToUser} className="w-full btn btn-primary">
                Assign
              </button>
            </div>
          </div>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-[680px] max-w-[95vw]">
            <h3 className="text-lg font-semibold mb-4">{editingSite ? 'Edit Site' : 'Add Site'}</h3>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-700">Name</label>
                <input
                  className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                  value={formData.name}
                  onChange={(e) => setFormData((p) => ({ ...p, name: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700">Code</label>
                <input
                  className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                  value={formData.code}
                  onChange={(e) => setFormData((p) => ({ ...p, code: e.target.value }))}
                />
              </div>
              <div className="col-span-2">
                <label className="block text-sm text-gray-700">Address</label>
                <input
                  className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                  value={formData.address}
                  onChange={(e) => setFormData((p) => ({ ...p, address: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700">City</label>
                <input
                  className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                  value={formData.city}
                  onChange={(e) => setFormData((p) => ({ ...p, city: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700">Postcode</label>
                <input
                  className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                  value={formData.postcode}
                  onChange={(e) => setFormData((p) => ({ ...p, postcode: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700">Phone</label>
                <input
                  className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                  value={formData.phone}
                  onChange={(e) => setFormData((p) => ({ ...p, phone: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-700">Email (optional)</label>
                <input
                  className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2"
                  value={formData.email}
                  onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))}
                />
              </div>
              <div className="col-span-2">
                <label className="inline-flex items-center mt-2">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData((p) => ({ ...p, isActive: e.target.checked }))}
                    className="rounded text-primary-600 focus:ring-primary-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Active</span>
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
              >
                Cancel
              </button>
              <button className="btn btn-primary" onClick={saveSite}>
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteManagement;
